import { useLocation } from "wouter"
import { FileText, Link2, Newspaper, Columns3, Image as ImageIcon } from "lucide-react"
import type { CockpitPost, CockpitPostStatus } from "@shared/dashboard"
import { channelAbbr, formatLabel, sourceLabel } from "@/lib/cockpitFormat"

// One post card in the week strip: thumbnail (image or tinted placeholder), channel badge,
// status dot, title + one-line subtitle. Click deep-links to the post's own surface.

const DOT: Record<CockpitPostStatus, string> = {
  published: "bg-primary",
  scheduled: "bg-zinc-400 dark:bg-zinc-500",
  waiting: "bg-orange-500",
  failed: "bg-red-500",
}

const STATUS_LABEL: Record<CockpitPostStatus, string> = {
  published: "veröffentlicht",
  scheduled: "geplant",
  waiting: "wartet auf Sie",
  failed: "fehlgeschlagen",
}

const TINT: Record<string, string> = {
  pillar_post: "from-emerald-100 to-emerald-200 dark:from-emerald-950 dark:to-emerald-900",
  industry_news: "from-sky-100 to-sky-200 dark:from-sky-950 dark:to-sky-900",
  repurposing_content: "from-violet-100 to-violet-200 dark:from-violet-950 dark:to-violet-900",
  content_upload: "from-amber-100 to-orange-200 dark:from-amber-950 dark:to-orange-950",
}

function PlaceholderArt({ source }: { source: string }) {
  const tint = TINT[source] ?? "from-zinc-100 to-zinc-200 dark:from-zinc-800 dark:to-zinc-700"
  const Icon =
    source === "industry_news" ? Newspaper
    : source === "repurposing_content" ? Link2
    : source === "pillar_post" ? Columns3
    : source === "content_upload" ? ImageIcon
    : FileText
  return (
    <div className={`absolute inset-0 grid place-items-center bg-gradient-to-br ${tint}`} aria-hidden="true">
      <Icon className="h-6 w-6 text-foreground/30" />
    </div>
  )
}

export function WeekPostCard({ post }: { post: CockpitPost }) {
  const [, setLocation] = useLocation()
  const subtitle = formatLabel(post.format) ?? post.pillarName ?? sourceLabel(post.source)

  return (
    <button
      type="button"
      onClick={() => post.href && setLocation(post.href)}
      disabled={!post.href}
      className="group flex w-full flex-col overflow-hidden rounded-lg border border-border/60 bg-card text-left transition-all hover:-translate-y-0.5 hover:shadow-md disabled:cursor-default disabled:hover:translate-y-0 disabled:hover:shadow-none"
      aria-label={`${post.title}, ${post.platform}, ${STATUS_LABEL[post.status]}`}
      data-testid={`week-post-${post.id}`}
    >
      <div className="relative aspect-[4/3] w-full overflow-hidden bg-muted">
        {post.imageUrl ? (
          <img src={post.imageUrl} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
        ) : (
          <PlaceholderArt source={post.source} />
        )}
        <span className="absolute left-1.5 top-1.5 rounded-full bg-zinc-900/80 px-1.5 py-0.5 text-[9.5px] font-bold tracking-[0.08em] text-white">
          {channelAbbr(post.platformKey)}
        </span>
        <span
          className={`absolute right-1.5 top-1.5 h-2.5 w-2.5 rounded-full border-2 border-white dark:border-zinc-900 ${DOT[post.status]}`}
          title={STATUS_LABEL[post.status]}
        />
      </div>
      <div className="px-2 pb-2 pt-1.5">
        <p className="line-clamp-2 text-[11.5px] leading-[1.35] text-foreground/90">{post.title}</p>
        <p className="mt-0.5 truncate text-[10.5px] text-muted-foreground">{subtitle}</p>
      </div>
    </button>
  )
}
